import * as React from 'react';
import type {HtmlEmbedOpenCanvasRewriter} from '@packages/systems/render';
import {identity} from '@packages/utilities/fp/utils';

/**
 * Context specific to the Open Canvas which is exposed in the Canvas App.
 *
 * - `isActive` — whether the canvas app is currently rendering Open Canvas
 *   (Multi-Context / Component Canvas). Drives viewport-unit rewriting in
 *   HTML insertion paths so units resolve against the active frame rather
 *   than the iframe viewport.
 * - `htmlEmbedOpenCanvasRewriter` — Open Canvas HtmlEmbed rewriter. Used to
 *   replace viewport units in inserted HTML so they resolve against the
 *   active frame rather than the iframe viewport.
 */
type OpenCanvasContextValue = {
  isActive: boolean;
  htmlEmbedOpenCanvasRewriter: HtmlEmbedOpenCanvasRewriter;
};

const DEFAULT_VALUE: OpenCanvasContextValue = {
  isActive: false,
  htmlEmbedOpenCanvasRewriter: identity,
};

const OpenCanvasContext =
  React.createContext<OpenCanvasContextValue>(DEFAULT_VALUE);

OpenCanvasContext.displayName = 'OpenCanvasContext';

export const OpenCanvasProvider = ({
  isActive,
  htmlEmbedOpenCanvasRewriter,
  children,
}: {
  isActive: boolean;
  htmlEmbedOpenCanvasRewriter: HtmlEmbedOpenCanvasRewriter;
  children: React.ReactNode;
}) => {
  const value = React.useMemo<OpenCanvasContextValue>(
    () => ({isActive, htmlEmbedOpenCanvasRewriter}),
    [isActive, htmlEmbedOpenCanvasRewriter]
  );
  return (
    <OpenCanvasContext.Provider value={value}>
      {children}
    </OpenCanvasContext.Provider>
  );
};

/**
 * Returns `true` when the canvas app is rendering Open Canvas, `false`
 * otherwise (including when called outside an `OpenCanvasProvider`).
 */
export const useIsOpenCanvasActive = (): boolean =>
  React.useContext(OpenCanvasContext).isActive;

/**
 * Returns the HtmlEmbed Open Canvas rewriter provided by the Canvas App.
 */
export const useHtmlEmbedOpenCanvasRewriter = (): HtmlEmbedOpenCanvasRewriter =>
  React.useContext(OpenCanvasContext).htmlEmbedOpenCanvasRewriter;
